import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Download, Save, X, BarChart3, FileStack, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import TemplateSelect from '@/components/TemplateSelect';
import { genId, emptyRow, calcRow, formatINR, getRoomTotal, defaultRoomNames } from '@/components/boq/boqCalc';
import RoomSpreadsheet, { GrandTotalView } from '@/components/boq/RoomSpreadsheet';
import ItemCostSheetPicker from '@/components/boq/ItemCostSheetPicker';

export default function BOQEstimateEditor({ projectId }) {
  const [rooms, setRooms] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // index or 'grand-total'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerRowIdx, setPickerRowIdx] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState(null);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [applyTemplateId, setApplyTemplateId] = useState('');
  const [applying, setApplying] = useState(false);

  const loadBOQ = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/boq`);
      if (res.data && res.data.length > 0) {
        setRooms(res.data.map(room => ({ ...room, items: (room.items || []).map(item => calcRow(item)) })));
      } else {
        setRooms([{ id: genId(), name: 'Living Room', items: [emptyRow(1), emptyRow(2), emptyRow(3)] }]);
      }
    } catch { toast.error('Failed to load BOQ'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadBOQ(); }, [loadBOQ]);

  const saveAll = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/projects/${projectId}/boq`, { rooms });
      setRooms(res.data.map(room => ({ ...room, items: (room.items || []).map(item => calcRow(item)) })));
      setDirty(false);
      toast.success('BOQ saved successfully');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  // Room operations
  const addRoom = (name) => {
    setRooms(prev => [...prev, { id: genId(), name, items: [emptyRow(1), emptyRow(2), emptyRow(3)] }]);
    setActiveTab(rooms.length);
    setDirty(true);
    setShowAddRoom(false);
  };
  const removeRoom = (idx) => {
    if (rooms.length <= 1) { toast.error('Need at least one room'); return; }
    setRooms(prev => prev.filter((_, i) => i !== idx));
    setActiveTab(prev => Math.max(0, typeof prev === 'number' ? Math.min(prev, rooms.length - 2) : 0));
    setDirty(true);
  };
  const renameRoom = (idx, name) => { setRooms(prev => prev.map((r, i) => i === idx ? { ...r, name } : r)); setDirty(true); };

  // Cell operations
  const updateCell = (roomIdx, rowIdx, key, value) => {
    setRooms(prev => {
      const updated = [...prev];
      const items = [...updated[roomIdx].items];
      items[rowIdx] = calcRow({ ...items[rowIdx], [key]: value });
      updated[roomIdx] = { ...updated[roomIdx], items };
      return updated;
    });
    setDirty(true);
  };
  const addRow = (roomIdx) => {
    setRooms(prev => {
      const updated = [...prev];
      const items = [...updated[roomIdx].items];
      items.push(emptyRow(items.length + 1));
      updated[roomIdx] = { ...updated[roomIdx], items };
      return updated;
    });
    setDirty(true);
  };
  const deleteRow = (roomIdx, rowIdx) => {
    setRooms(prev => {
      const updated = [...prev];
      let items = updated[roomIdx].items.filter((_, i) => i !== rowIdx);
      items = items.map((item, i) => ({ ...item, sno: i + 1 }));
      updated[roomIdx] = { ...updated[roomIdx], items };
      return updated;
    });
    setDirty(true);
  };

  const applyDbSelection = (item, variant) => {
    if (pickerRowIdx === null || typeof activeTab !== 'number') return;
    setRooms(prev => {
      const updated = [...prev];
      const items = [...updated[activeTab].items];
      const row = { ...items[pickerRowIdx] };
      row.description = item.item_name;
      row.specification = variant.specification;
      row.dnb_item_id = item.id;
      row.dnb_variant_id = variant.id;
      row.unit = variant.uom;
      row.rate = variant.rate;
      items[pickerRowIdx] = calcRow(row);
      updated[activeTab] = { ...updated[activeTab], items };
      return updated;
    });
    setDirty(true);
    setShowPicker(false);
    toast.success(`Applied: ${item.item_name} — ${variant.specification}`);
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    rooms.forEach(room => {
      const data = room.items.map(item => ({
        'Sno': item.sno, 'Description': item.description, 'Specification': item.specification || '',
        'L': item.l || '', 'W': item.w || '', 'H': item.h || '', 'No.': item.nos || 1,
        'QNTY': item.qty, 'Unit': item.unit, 'RATE': item.rate, 'AMOUNT': item.amount,
      }));
      const total = getRoomTotal(room);
      data.push({ 'Sno': '', Description: '', Specification: 'ROOM TOTAL', 'L': '', 'W': '', 'H': '', 'No.': '', 'QNTY': '', 'Unit': '', 'RATE': '', 'AMOUNT': total });
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 25 }, { wch: 7 }, { wch: 7 }, { wch: 7 }, { wch: 5 }, { wch: 10 }, { wch: 8 }, { wch: 10 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws, room.name.slice(0, 31));
    });
    const gtData = rooms.map(r => ({ 'Room': r.name, 'Total (Rs)': getRoomTotal(r) }));
    gtData.push({ 'Room': 'GRAND TOTAL', 'Total (Rs)': grandTotal });
    const gtWs = XLSX.utils.json_to_sheet(gtData);
    gtWs['!cols'] = [{ wch: 25 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, gtWs, 'Grand Total');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `BOQ-Estimate-${projectId.slice(0, 8)}.xlsx`);
    toast.success('Excel exported');
  };

  const applyTemplate = async () => {
    if (!applyTemplateId) { toast.error('Select a template first'); return; }
    setApplying(true);
    try {
      const res = await api.post(`/templates/${applyTemplateId}/apply`, { project_id: projectId });
      const processed = (res.data || []).map(room => ({ ...room, items: (room.items || []).map(item => calcRow(item)) }));
      setRooms(processed.length > 0 ? processed : [{ id: genId(), name: 'Living Room', items: [emptyRow(1)] }]);
      setActiveTab(0);
      setDirty(false);
      setShowApplyTemplate(false);
      setApplyTemplateId('');
      toast.success('Template applied — BOQ regenerated');
    } catch { toast.error('Failed to apply template'); }
    finally { setApplying(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  const grandTotal = rooms.reduce((s, r) => s + getRoomTotal(r), 0);
  const currentRoom = typeof activeTab === 'number' ? rooms[activeTab] : null;
  const currentRoomTotal = currentRoom ? getRoomTotal(currentRoom) : 0;
  const availableRoomNames = defaultRoomNames.filter(n => !rooms.some(r => r.name === n));

  return (
    <div className="space-y-3" data-testid="boq-estimate-editor">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="section-heading text-sm font-semibold font-['Manrope']">BOQ / Estimate</h3>
          {dirty && <span className="text-[10px] text-[#C67D5B] bg-[#C67D5B]/10 px-2 py-0.5 rounded-full">Unsaved</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setApplyTemplateId(''); setShowApplyTemplate(true); }} data-testid="boq-apply-template-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors">
            <FileStack size={13} strokeWidth={1.5} /> Apply Template
          </button>
          <button onClick={exportExcel} data-testid="boq-export-excel"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors">
            <Download size={13} strokeWidth={1.5} /> Export
          </button>
          <button onClick={saveAll} disabled={saving} data-testid="boq-save-btn"
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors disabled:opacity-50">
            <Save size={13} strokeWidth={1.5} /> {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Room tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="boq-room-tabs">
        {rooms.map((room, idx) => (
          <button key={room.id} onClick={() => setActiveTab(idx)} onDoubleClick={() => setEditingRoomName(idx)}
            className={`group relative px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all whitespace-nowrap border-b-2
              ${activeTab === idx ? 'bg-white text-[#3A8C8C] border-[#3A8C8C] shadow-sm' : 'bg-[#F5F3EF] text-[#6B7280] border-transparent hover:bg-[#E8E6E1]'}`}
            data-testid={`boq-room-tab-${idx}`}>
            {editingRoomName === idx ? (
              <input autoFocus value={room.name} onChange={e => renameRoom(idx, e.target.value)}
                onBlur={() => setEditingRoomName(null)} onKeyDown={e => e.key === 'Enter' && setEditingRoomName(null)}
                className="w-24 bg-transparent outline-none text-xs" onClick={e => e.stopPropagation()} />
            ) : (<span>{room.name}</span>)}
            {rooms.length > 1 && activeTab === idx && (
              <button onClick={e => { e.stopPropagation(); removeRoom(idx); }}
                className="ml-1.5 opacity-0 group-hover:opacity-100 text-[#C4727F] hover:text-[#C4727F] transition-opacity" data-testid={`boq-remove-room-${idx}`}>
                <X size={11} strokeWidth={2} />
              </button>
            )}
          </button>
        ))}
        <button onClick={() => setActiveTab('grand-total')}
          className={`px-3 py-1.5 rounded-t-lg text-xs font-semibold transition-all whitespace-nowrap border-b-2 flex items-center gap-1.5
            ${activeTab === 'grand-total' ? 'bg-white text-[#3A8C8C] border-[#3A8C8C] shadow-sm' : 'bg-[#F5F3EF] text-[#6B7280] border-transparent hover:bg-[#E8E6E1]'}`}
          data-testid="boq-grand-total-tab">
          <BarChart3 size={12} strokeWidth={1.5} /> Grand Total
        </button>
        <button onClick={() => setShowAddRoom(true)} className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" data-testid="boq-add-room-btn">
          <Plus size={14} strokeWidth={1.5} className="text-[#3A8C8C]" />
        </button>
      </div>

      {activeTab === 'grand-total' && <GrandTotalView rooms={rooms} grandTotal={grandTotal} />}

      {typeof activeTab === 'number' && currentRoom && (
        <RoomSpreadsheet
          room={currentRoom} roomIdx={activeTab} roomTotal={currentRoomTotal} grandTotal={grandTotal}
          onUpdateCell={updateCell} onAddRow={addRow} onDeleteRow={deleteRow}
          onOpenPicker={(rowIdx) => { setPickerRowIdx(rowIdx); setShowPicker(true); }}
        />
      )}

      <ItemCostSheetPicker open={showPicker} onOpenChange={setShowPicker} onSelect={applyDbSelection} />

      {/* Add Room Dialog */}
      <Dialog open={showAddRoom} onOpenChange={setShowAddRoom}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Add Room</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {availableRoomNames.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Quick Add</p>
                <div className="flex flex-wrap gap-1.5">
                  {availableRoomNames.slice(0, 8).map(name => (
                    <button key={name} onClick={() => addRoom(name)}
                      className="px-2.5 py-1 rounded-md text-xs bg-[#F5F3EF] text-[#2C2C2E] hover:bg-[#3A8C8C]/10 hover:text-[#3A8C8C] transition-colors"
                      data-testid={`boq-quick-add-${name.replace(/\s/g, '-')}`}>{name}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Custom Name</p>
              <div className="flex gap-2">
                <Input id="custom-room-name" placeholder="e.g. Terrace, Pooja Room" className="h-9 text-sm rounded-lg border-[#D4D0C8]"
                  data-testid="boq-custom-room-input"
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) addRoom(e.target.value.trim()); }} />
                <button onClick={() => { const el = document.getElementById('custom-room-name'); if (el?.value.trim()) addRoom(el.value.trim()); }}
                  className="px-3 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
                  data-testid="boq-add-custom-room-btn">Add</button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Apply Template Dialog */}
      <Dialog open={showApplyTemplate} onOpenChange={setShowApplyTemplate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-base flex items-center gap-2">
              <FileStack size={16} className="text-[#3A8C8C]" /> Apply Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <TemplateSelect value={applyTemplateId} onChange={(id) => setApplyTemplateId(id)} />
            <div className="flex items-start gap-2 px-3 py-2.5 bg-[#C4727F]/10 border border-[#C4727F]/20 rounded-lg">
              <AlertTriangle size={15} className="text-[#C4727F] shrink-0 mt-0.5" />
              <p className="text-xs text-[#C4727F]">Applying this template will overwrite existing BOQ data in this project. This action cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowApplyTemplate(false)}
                className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
              <button onClick={applyTemplate} disabled={applying || !applyTemplateId} data-testid="boq-confirm-apply-template"
                className="flex-1 h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] disabled:opacity-50">
                {applying ? 'Applying…' : 'Apply Template'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
